import { useTranslation } from 'next-i18next';
import Router, { useRouter } from 'next/router';
import { memo, useEffect, useMemo } from 'react';
import { Icon } from '@bridebook/aura';
import { HeartOutline, Home, MessageLeft, Search, Sparkle } from '@bridebook/aura/src/icons';
import { Tabs } from '@bridebook/ui';
import { useMarket } from '@bridebook/web/app-shared/lib/i18n/hooks/use-market';
import BottomTabMobile, { BottomTab } from '@bridebook/web/components/mobile-app/bottom-tab-mobile';
import { getScreenType } from '@bridebook/web/lib/app/selectors';
import { scrollToTop } from '@bridebook/web/lib/bbcommon/utils/animations';
import { getCheckDirectoryLoaded } from '@bridebook/web/lib/i18n/selectors';
import { navigateToVenuesAndSuppliers } from '@bridebook/web/lib/search-landing/actions';
import { isGlobalLocationsPage, isSearchResultsPage } from '@bridebook/web/lib/search/utils';
import {
  showMobileNav,
  usedHeaderHomeButton,
  usedHeaderNavigation,
} from '@bridebook/web/lib/ui/actions';
import { getIsMobileNavShown, ProtectedUrls, UrlHelper } from '@bridebook/web/lib/url-helper';
import { useDispatch, useSelector } from '@bridebook/web/lib/utils';
import { getInboxUnreadCount } from '@bridebook/web/lib/weddings/selectors';
import { InboxView } from '@bridebook/web/components/inbox/context/types';
import { MOBILE_NAV_HEIGHT } from '@bridebook/web/lib/mobile-app/constants';

const ICON_SIZE = 21;

const MobileNavigation = () => {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const mobileNavShown = useSelector((state) => state.ui.mobileNavShown);
  const appKeyboardOpened = useSelector((state) => state.mobileapp.appKeyboardOpened);
  const checkDirectoryLoaded = useSelector(getCheckDirectoryLoaded);
  const { isMobile } = useSelector(getScreenType);

  const router = useRouter();
  const market = useMarket();
  // App nav only visible for mobile logged-in users and non-excluded routes
  const showNav =
    isMobile && checkDirectoryLoaded ? getIsMobileNavShown(router.asPath, market.prefix) : false;

  useEffect(() => {
    if (showNav !== mobileNavShown) {
      dispatch(showMobileNav(showNav));
    }
  }, [dispatch, mobileNavShown, showNav]);

  const menuList = useMemo(() => {
    if (!mobileNavShown || !checkDirectoryLoaded) return [];

    const navigate: BottomTab['onClick'] = (_, bottomTab) => {
      if (!bottomTab.url) throw new Error('Url not provided to bottom tab option');
      dispatch(usedHeaderNavigation(bottomTab.destination));
      if (bottomTab.id === 'home') {
        dispatch(usedHeaderHomeButton('mobileHomeTab'));
      }
      Router.push(bottomTab.url).then(() => {
        scrollToTop();
      });
    };

    const menu: Record<
      'home' | 'suppliers' | 'messages' | 'favourites' | 'inspiration',
      BottomTab
    > = {
      home: {
        id: 'home',
        destination: 'home',
        url: ProtectedUrls.home,
        title: t('menu.home'),
        icon: <Icon icon={Home} size={ICON_SIZE} className="text-space-60" />,
        iconActive: <Icon icon={Home} size={ICON_SIZE} className="text-indigo-crush" />,
        onClick: navigate,
      },
      suppliers: {
        id: 'search-landing',
        title: t('menu.search'),
        destination: 'suppliers',
        icon: <Icon icon={Search} size={ICON_SIZE} className="text-space-60" />,
        iconActive: <Icon icon={Search} size={ICON_SIZE} className="text-indigo-crush" />,
        onClick: () => {
          dispatch(usedHeaderNavigation('suppliers'));
          dispatch(navigateToVenuesAndSuppliers({ navigateToRecent: false }));
        },
        isActive: ({ pathname, serverPage }) =>
          serverPage.includes('search') ||
          isGlobalLocationsPage(pathname) ||
          isSearchResultsPage(pathname),
      },
      favourites: {
        id: 'shortlist',
        destination: 'shortlist',
        url: ProtectedUrls.shortlist.main,
        title: t('menu.shortlist'),
        onClick: navigate,
        icon: <Icon icon={HeartOutline} size={ICON_SIZE} className="text-space-60" />,
        iconActive: <Icon icon={HeartOutline} size={ICON_SIZE} className="text-indigo-crush" />,
      },
      messages: {
        id: 'messages',
        destination: 'messages',
        url: ProtectedUrls.inbox[InboxView.Suppliers].main,
        title: t('menu.messages'),
        icon: <MessagesIcon />,
        iconActive: <MessagesIcon active />,
        isActive: ({ pathname }) => pathname.startsWith(UrlHelper.inbox.main),
        onClick: navigate,
      },
      inspiration: {
        id: 'inspiration',
        destination: 'inspiration',
        url: ProtectedUrls.inspiration,
        title: t('menu.inspiration'),
        icon: <Icon icon={Sparkle} size={ICON_SIZE} className="text-space-60" />,
        iconActive: <Icon icon={Sparkle} size={ICON_SIZE} className="text-indigo-crush" />,
        onClick: navigate,
        isActive: ({ pathname }) => pathname.startsWith('/inspiration'),
      },
    };

    return [menu.home, menu.inspiration, menu.suppliers, menu.favourites, menu.messages];
  }, [checkDirectoryLoaded, dispatch, mobileNavShown, t]);

  if (
    !mobileNavShown ||
    appKeyboardOpened ||
    !checkDirectoryLoaded ||
    !menuList ||
    menuList.length === 0
  ) {
    return null;
  }

  return (
    <div
      data-name="app-navigation"
      style={{ height: `calc(${MOBILE_NAV_HEIGHT}px + env(safe-area-inset-bottom))` }}
      className="fixed bottom-0 z-[999] w-full bg-white shadow-[0_-4px_10px_rgba(0,0,0,0.05)]">
      <Tabs>
        {menuList.map((menuTab) => (
          <BottomTabMobile key={menuTab.id} bottomTab={menuTab} />
        ))}
      </Tabs>
    </div>
  );
};

const MessagesIcon = ({ active }: { active?: boolean }) => {
  const inboxUnreadCount = useSelector(getInboxUnreadCount);
  return (
    <div data-name="app-bar-messages-icon" className="relative">
      <Icon
        icon={MessageLeft}
        size={ICON_SIZE}
        className={active ? 'text-indigo-crush' : 'text-space-60'}
      />
      {inboxUnreadCount > 0 && (
        <span className="absolute -top-1.5 right-0 flex translate-x-1/2 items-center justify-center rounded-2xl bg-alerts px-1 py-0.5 typo-xs-semibold leading-none text-white select-none">
          {inboxUnreadCount > 99 ? '99+' : inboxUnreadCount}
        </span>
      )}
    </div>
  );
};

export default memo(MobileNavigation);
